<template>
  <div class="register-list">
    <header class="page-header">
      <div class="header-main">
        <div class="header-icon">
          <i class="bi bi-check-circle-fill" style="font-size:22px" aria-hidden="true"></i>
        </div>
        <div class="header-text">
          <div class="header-title-row">
            <h1 class="page-title">Registered influencers</h1>
            <span class="stat-chip">
              <span class="stat-dot"></span>
              <strong>{{ influencers.length }}</strong>{{ hasNextPage ? '+' : '' }} on page
            </span>
            <span class="stat-chip stat-chip-warn" v-if="pendingReviewCount > 0">
              <strong>{{ pendingReviewCount }}</strong> new submission{{ pendingReviewCount === 1 ? '' : 's' }}
            </span>
            <span class="stat-chip stat-chip-muted" v-if="status !== null">
              {{ getStatusLabel(status) }}
            </span>
          </div>
          <p class="page-subtitle">Submissions from the public registration form — review and mark as processed.</p>
        </div>
      </div>
    </header>

    <div class="filters-card">
      <div class="filter-label-row">
        <span class="filter-label">Status</span>
        <button class="reset-link" v-if="status !== null" @click="status = null">Reset</button>
      </div>
      <div class="status-tabs">
        <button
          v-for="opt in statusOptions"
          :key="String(opt.value)"
          class="tab"
          :class="{ 'is-active': status === opt.value }"
          @click="status = opt.value"
        >
          <span class="tab-dot" :class="'dot-' + opt.tone"></span>
          {{ opt.label }}
        </button>
      </div>
    </div>

    <div class="table-card" :class="{ 'is-loading': loading }">
      <div class="table-wrap" v-if="!loading && influencers.length">
        <table class="data-table">
          <thead>
            <tr>
              <th class="col-id">#</th>
              <th class="col-influencer">Influencer</th>
              <th class="col-channels">Channels</th>
              <th class="col-date">Submitted</th>
              <th class="col-status">Status</th>
              <th class="col-actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(influencer, $index) in influencers" :key="influencer.id || $index">
              <td class="col-id">
                <span class="id-badge">{{ rangeStart + $index }}</span>
              </td>
              <td class="col-influencer">
                <div class="inf-name">{{ influencer.nickname || '—' }}</div>
                <a
                  v-if="influencer.email"
                  class="inf-email"
                  :href="'mailto:' + influencer.email"
                  :title="influencer.email"
                >
                  <i class="bi bi-envelope-fill" aria-hidden="true"></i>
                  {{ influencer.email }}
                </a>
              </td>
              <td class="col-channels">
                <div class="channels-list" v-if="parseChannels(influencer.channels).length">
                  <a
                    v-for="(ch, i) in parseChannels(influencer.channels)"
                    :key="'ch' + i"
                    class="channel-pill"
                    :href="ch.url"
                    target="_blank"
                    rel="noopener"
                    :title="ch.url"
                  >
                    <img v-if="ch.platform" :src="platformIconSrc(ch.platform)" :alt="ch.platform" class="channel-icon"/>
                    <span v-else class="channel-icon channel-icon-fallback">?</span>
                    <span class="channel-text">{{ ch.label }}</span>
                  </a>
                </div>
                <span v-else class="muted">—</span>
              </td>
              <td class="col-date">
                <div class="date-main">{{ formatDate(influencer.time) }}</div>
                <div class="date-rel">{{ formatRelative(influencer.time) }}</div>
              </td>
              <td class="col-status">
                <span class="status-pill" :class="'tone-' + statusTone(influencer.status)">
                  <span class="pill-dot"></span> {{ getStatusById(influencer.status) }}
                </span>
              </td>
              <td class="col-actions">
                <button
                  v-if="influencer.status === 0"
                  class="action-btn action-accept"
                  @click="markReviewed($index, influencer.id)"
                  :disabled="isPending(influencer.id)"
                  title="Mark as reviewed"
                >
                  <span v-if="isPending(influencer.id)" class="spinner"></span>
                  <i class="bi bi-check-lg" v-else aria-hidden="true"></i>
                  Mark as reviewed
                </button>
                <span v-else class="muted">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="skeleton-list" v-if="loading">
        <div class="skeleton-row" v-for="n in 5" :key="'sk' + n">
          <div class="skeleton skeleton-id"></div>
          <div class="skeleton-stack">
            <div class="skeleton skeleton-line skeleton-line-lg"></div>
            <div class="skeleton skeleton-line skeleton-line-sm"></div>
          </div>
          <div class="skeleton skeleton-line"></div>
          <div class="skeleton skeleton-line skeleton-line-md"></div>
          <div class="skeleton skeleton-pill"></div>
          <div class="skeleton skeleton-btn"></div>
        </div>
      </div>

      <div class="empty-state" v-if="!loading && !influencers.length">
        <i class="bi bi-file-earmark-x" style="font-size:44px" aria-hidden="true"></i>
        <h3>No registrations</h3>
        <p v-if="status !== null">No submissions match this status filter.</p>
        <p v-else>The registration queue is empty.</p>
        <button v-if="status !== null" class="btn btn-outline-secondary" @click="status = null">Show all</button>
      </div>
    </div>

    <div class="pagination-bar" v-if="influencers.length || page > 1">
      <div class="pagination-left">
        <label class="per-page-label">Per page</label>
        <select class="form-control per-page" v-model.number="limit">
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
          <option :value="200">200</option>
        </select>
      </div>
      <div class="pagination-center">
        <button class="page-btn" @click="goToPage(page - 1)" :disabled="page <= 1">
          <i class="bi bi-chevron-left" aria-hidden="true"></i>
          Previous
        </button>
        <span class="page-indicator">Page <strong>{{ page }}</strong></span>
        <button class="page-btn" @click="goToPage(page + 1)" :disabled="!hasNextPage">
          Next
          <i class="bi bi-chevron-right" aria-hidden="true"></i>
        </button>
      </div>
      <div class="pagination-right"></div>
    </div>
  </div>
</template>

<script>
  import { detectPlatformHint } from '../../../utils/socialChannels';

  export default {
    name: "influencer-register-list",
    data() {
      return {
        influencers: [],
        limit: 50,
        page: 1,
        status: null,
        loading: false,
        pendingIds: [],
        apiHost: this.$appSettings.getSetting('host'),
        statusOptions: [
          { value: null, label: 'All', tone: 'neutral' },
          { value: 0, label: 'New', tone: 'info' },
          { value: 1, label: 'Accepted', tone: 'success' },
          { value: 2, label: 'Rejected', tone: 'danger' }
        ]
      }
    },
    computed: {
      rangeStart() {
        return (this.page - 1) * this.limit + 1;
      },
      hasNextPage() {
        return this.influencers.length === this.limit;
      },
      pendingReviewCount() {
        return this.influencers.filter(i => i.status === 0).length;
      }
    },
    watch: {
      status() {
        this.page = 1;
        this.getInfluencers();
      },
      limit() {
        this.page = 1;
        this.getInfluencers();
      }
    },
    methods: {
      getInfluencers() {
        this.loading = true;
        const params = {
          limit: this.limit,
          offset: (this.page - 1) * this.limit,
          token: this.$auth.getToken()
        };
        if (this.status !== null) params.status = this.status;
        this.$http.get(this.apiHost + 'influ/list', { params: params }).then(response => {
          this.loading = false;
          this.influencers = response.body.list || [];
        }, () => {
          this.loading = false;
        });
      },
      goToPage(p) {
        if (p < 1) return;
        if (p > this.page && !this.hasNextPage) return;
        this.page = p;
        this.getInfluencers();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      markReviewed($idx, id) {
        if (this.isPending(id)) return;
        this.pendingIds.push(id);
        this.$http.put(this.apiHost + 'influ/change/' + id + '/status/1', {}, {
          params: { token: this.$auth.getToken() }
        }).then(() => {
          this.removePending(id);
          this.influencers[$idx].status = 1;
          this.$notify({ group: 'default', type: 'success', text: 'Marked as reviewed' });
        }, () => {
          this.removePending(id);
          this.$notify({ group: 'default', type: 'error', text: 'Update failed' });
        });
      },
      removePending(id) {
        const i = this.pendingIds.indexOf(id);
        if (i !== -1) this.pendingIds.splice(i, 1);
      },
      isPending(id) {
        return this.pendingIds.indexOf(id) !== -1;
      },
      parseChannels(raw) {
        if (!raw || typeof raw !== 'string') return [];
        return raw
          .split(/[\s,]+/)
          .map(s => s.trim())
          .filter(Boolean)
          .map(url => {
            const platform = detectPlatformHint(url) || '';
            return { url, platform, label: this.shortUrl(url, platform) };
          });
      },
      shortUrl(url, platform) {
        try {
          const stripped = url.replace(/^https?:\/\/(www\.|m\.|web\.|vm\.|vt\.)?/i, '');
          const parts = stripped.split('/').filter(Boolean);
          if (parts.length >= 2) {
            const handle = parts[parts.length - 1].replace(/^@/, '');
            if (handle && handle.length < 40) return '@' + handle;
          }
          return parts.join('/');
        } catch (e) {
          return url;
        }
      },
      platformIconSrc(platform) {
        try {
          return require('../../../assets/platforms/' + platform + '.png');
        } catch (e) {
          return '';
        }
      },
      getStatusById(id) {
        const opt = this.statusOptions.find(o => o.value === id);
        return opt ? opt.label : 'Unset';
      },
      getStatusLabel(value) {
        const opt = this.statusOptions.find(o => o.value === value);
        return opt ? opt.label : '';
      },
      statusTone(id) {
        const opt = this.statusOptions.find(o => o.value === id);
        return opt ? opt.tone : 'neutral';
      },
      formatDate(unixtimestamp) {
        if (!unixtimestamp) return '—';
        const date = new Date(unixtimestamp * 1000);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      },
      formatRelative(unixtimestamp) {
        if (!unixtimestamp) return '';
        const diffSec = Math.floor(Date.now() / 1000) - unixtimestamp;
        if (diffSec < 60) return 'just now';
        if (diffSec < 3600) return Math.floor(diffSec / 60) + 'm ago';
        if (diffSec < 86400) return Math.floor(diffSec / 3600) + 'h ago';
        if (diffSec < 86400 * 30) return Math.floor(diffSec / 86400) + 'd ago';
        if (diffSec < 86400 * 365) return Math.floor(diffSec / (86400 * 30)) + 'mo ago';
        return Math.floor(diffSec / (86400 * 365)) + 'y ago';
      }
    },
    created() {
      this.getInfluencers();
    }
  }
</script>

<style scoped>
  .register-list {
    max-width: 1280px;
    margin: 0 auto;
    color: #1f2937;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 22px;
    padding: 22px 24px;
    background: linear-gradient(135deg, #ecfdf5 0%, #ffffff 60%);
    border: 1px solid #a7f3d0;
    border-radius: 16px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    position: relative;
    overflow: hidden;
  }
  .header-main {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex: 1;
  }
  .header-icon {
    flex: 0 0 48px;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: #d1fae5;
    color: #047857;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .header-title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 6px;
  }
  .page-title {
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0;
    color: #0f172a;
    line-height: 1.1;
  }
  .stat-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #475569;
    border-radius: 999px;
    padding: 3px 10px 3px 8px;
    font-size: 12px;
    font-weight: 500;
  }
  .stat-chip strong { color: #0f172a; font-weight: 700; }
  .stat-chip-warn {
    background: #dbeafe;
    border-color: #bfdbfe;
    color: #1d4ed8;
  }
  .stat-chip-warn strong { color: #1e3a8a; }
  .stat-chip-muted {
    background: rgba(6, 95, 70, 0.08);
    border-color: rgba(6, 95, 70, 0.18);
    color: #065f46;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 10.5px;
    font-weight: 700;
    padding: 3px 10px;
  }
  .stat-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18);
  }
  .page-subtitle {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }

  .filters-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 14px 18px;
    margin-bottom: 18px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
  .filter-label-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }
  .filter-label {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .reset-link {
    background: transparent;
    border: 0;
    color: #265a88;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    padding: 2px 8px;
    border-radius: 6px;
  }
  .reset-link:hover { background: #eaf1f9; }
  .status-tabs {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #475569;
    border-radius: 999px;
    padding: 6px 14px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s, box-shadow 0.15s;
  }
  .tab:hover {
    border-color: #265a88;
    color: #265a88;
  }
  .tab.is-active {
    background: #265a88;
    border-color: #265a88;
    color: #fff;
    box-shadow: 0 2px 6px rgba(38, 90, 136, 0.25);
  }
  .tab.is-active .tab-dot { background: #fff; }
  .tab-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }
  .dot-neutral { background: #94a3b8; }
  .dot-info { background: #3b82f6; }
  .dot-success { background: #10b981; }
  .dot-danger { background: #ef4444; }

  .table-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
  .table-wrap { overflow-x: auto; }
  .data-table {
    width: 100%;
    border-collapse: collapse;
  }
  .data-table thead th {
    background: #fafbfc;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    padding: 12px 16px;
    font-size: 11.5px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .data-table tbody td {
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 14px;
    vertical-align: top;
  }
  .data-table tbody tr:last-child td { border-bottom: 0; }
  .data-table tbody tr:hover { background: #fafbfc; }
  .col-id { width: 70px; }
  .col-influencer { width: 220px; }
  .col-channels { min-width: 280px; }
  .col-date { width: 170px; }
  .col-status { width: 140px; }
  .col-actions { width: 170px; text-align: right; }

  .id-badge {
    display: inline-block;
    background: #f1f5f9;
    color: #475569;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 6px;
  }
  .inf-name {
    font-weight: 600;
    color: #0f172a;
    font-size: 14px;
    line-height: 1.2;
  }
  .inf-email {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 4px;
    font-size: 12.5px;
    color: #6b7280;
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
  .inf-email .bi { color: #6b7280; flex-shrink: 0; }
  .inf-email:hover {
    color: #265a88;
    text-decoration: underline;
  }

  .channels-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  .channel-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 4px;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    font-size: 12.5px;
    color: #0f172a;
    text-decoration: none;
    max-width: 240px;
    transition: border-color 0.15s, background 0.15s;
  }
  .channel-pill:hover {
    border-color: #265a88;
    background: #fff;
  }
  .channel-icon {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    flex-shrink: 0;
  }
  .channel-icon-fallback {
    background: #f1f5f9;
    color: #6b7280;
    font-weight: 600;
    font-size: 11px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .channel-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .date-main {
    font-size: 13px;
    color: #0f172a;
    line-height: 1.2;
  }
  .date-rel {
    font-size: 11.5px;
    color: #6b7280;
    margin-top: 2px;
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid transparent;
  }
  .pill-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
  .tone-neutral { background: #f1f5f9; color: #475569; border-color: #e2e8f0; }
  .tone-info { background: #dbeafe; color: #1d4ed8; border-color: #bfdbfe; }
  .tone-success { background: #d1fae5; color: #065f46; border-color: #a7f3d0; }
  .tone-danger { background: #fee2e2; color: #991b1b; border-color: #fecaca; }

  .muted { color: #9ca3af; }

  .action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #d1fae5;
    border: 1px solid #a7f3d0;
    color: #065f46;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s, opacity 0.15s;
    min-width: 132px;
    justify-content: center;
  }
  .action-btn:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
  .action-accept:hover:not(:disabled) {
    background: #10b981;
    border-color: #10b981;
    color: #fff;
  }

  .spinner {
    display: inline-block;
    width: 11px;
    height: 11px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .skeleton-list { padding: 8px 0; }
  .skeleton-row {
    display: grid;
    grid-template-columns: 70px 220px 1fr 170px 140px 170px;
    gap: 16px;
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
    align-items: center;
  }
  .skeleton {
    background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 6px;
  }
  .skeleton-id { width: 50px; height: 22px; }
  .skeleton-line { height: 14px; width: 80%; }
  .skeleton-line-lg { height: 14px; width: 70%; }
  .skeleton-line-sm { height: 10px; width: 90%; margin-top: 6px; }
  .skeleton-line-md { height: 12px; width: 60%; }
  .skeleton-stack > * + * { margin-top: 4px; }
  .skeleton-pill { width: 100px; height: 26px; border-radius: 999px; }
  .skeleton-btn { width: 140px; height: 28px; justify-self: end; }
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  .empty-state {
    padding: 50px 20px;
    text-align: center;
    color: #6b7280;
  }
  .empty-state .bi { margin-bottom: 12px; }
  .empty-state h3 {
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 6px;
  }
  .empty-state p {
    margin: 0 0 16px;
    font-size: 13.5px;
  }

  .pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 4px;
  }
  .pagination-left, .pagination-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    font-size: 13px;
    color: #6b7280;
  }
  .pagination-right { justify-content: flex-end; }
  .per-page {
    width: auto;
    min-width: 70px;
    padding: 6px 28px 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 13px;
    background: #fff;
  }
  .pagination-center {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .page-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #374151;
    font-size: 13px;
    font-weight: 500;
    padding: 6px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
  }
  .page-btn:hover:not(:disabled) {
    border-color: #265a88;
    color: #265a88;
  }
  .page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .page-indicator { font-size: 13px; color: #6b7280; padding: 0 8px; }
  .page-indicator strong { color: #0f172a; font-weight: 600; }

  @media (max-width: 900px) {
    .data-table thead { display: none; }
    .data-table tbody tr { display: grid; grid-template-columns: auto 1fr; gap: 6px 12px; padding: 12px 14px; }
    .data-table tbody td { border-bottom: 0; padding: 4px 0; }
    .col-channels, .col-actions { grid-column: 1 / -1; }
    .col-actions { text-align: left; }
    .action-btn { width: 100%; }
  }
</style>



// WEBPACK FOOTER //
// src/components/influencer/register/InfluencerRegisterList.vue